import React, { useState, useEffect, Fragment } from "react";
import { Dialog, Transition } from "@headlessui/react";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import ApiClient from "../../methods/api/apiClient";
import loader from "../../methods/loader";
import swal from "sweetalert";
import { useNavigate } from "react-router-dom";

// ─── Empty form state ─────────────────────────────────────────────
const emptyForm = {
  name: "",
  email: "",
  phoneNumber: "",
  comment: "",
};

// ─── Validators ───────────────────────────────────────────────────
const isValidEmail = (val) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val.trim());

// phoneNumber is stored as dialCode + number (e.g. "917018596132")
// Minimum 7 digits after stripping the dial code prefix is a reasonable sanity check.
// We also accept any length between 7–15 digits (ITU-T E.164).
const isValidPhone = (val) => {
  const digits = val.replace(/\D/g, "");
  return digits.length >= 7 && digits.length <= 15;
};

// ─── FormInput ────────────────────────────────────────────────────
const FormInput = ({
  label,
  name,
  type = "text",
  required,
  textarea,
  value,
  onChange,
  error,
  placeholder,
}) => (
  <div>
    <label className="block text-sm text-gray-700 mb-1">
      {label} {required && <span className="text-red-400">*</span>}
    </label>
    {textarea ? (
      <textarea
        name={name}
        value={value}
        onChange={onChange}
        rows={4}
        placeholder={placeholder}
        className={`w-full border rounded-xl px-3 py-2 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-[#115B9D]/30 focus:border-[#115B9D] transition-all resize-none ${error ? "border-red-400" : "border-gray-200"
          }`}
      />
    ) : (
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className={`w-full border rounded-xl px-3 py-2 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-[#115B9D]/30 focus:border-[#115B9D] transition-all h-11 ${error ? "border-red-400" : "border-gray-200"
          }`}
      />
    )}
    {error && <span role="alert" className="text-xs text-[#c53030] mt-1 block">{error}</span>}
  </div>
);

// ─── Main Component ───────────────────────────────────────────────
/**
 * QuoteRequestModal
 *
 * Props:
 *  - open      {boolean}   — controls modal visibility
 *  - setOpen   {function}  — setter to open / close the modal
 *  - onSuccess {function}  — optional callback fired after a successful submission
 *
 * Usage:
 *  const [quoteOpen, setQuoteOpen] = useState(false);
 *  <QuoteRequestModal open={quoteOpen} setOpen={setQuoteOpen} onSuccess={() => refetch()} />
 *  <button onClick={() => setQuoteOpen(true)}>Get a Quote</button>
 */
const QuoteRequestModal = ({ open, setOpen, onSuccess }) => {
  const navigate = useNavigate();
  const [form, setForm] = useState(emptyForm);
  const [formErrors, setFormErrors] = useState({});
  // dialCode tracks just the country code so we can validate the number part separately
  const [dialCode, setDialCode] = useState("1");

  // Reset form whenever modal opens
  useEffect(() => {
    if (open) {
      setForm(emptyForm);
      setFormErrors({});
      setDialCode("1");
    }
  }, [open]);

  const closeModal = () => {
    setOpen(false);
    setForm(emptyForm);
    setFormErrors({});
    setDialCode("1");
  };

  // ── Field change ──
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (formErrors[name]) setFormErrors((prev) => ({ ...prev, [name]: "" }));
  };

  // ── Phone change (react-phone-input-2 returns fullNumber, countryData) ──
  const handlePhoneChange = (fullNumber, countryData) => {
    setDialCode(countryData?.dialCode || "");
    setForm((prev) => ({ ...prev, phoneNumber: fullNumber }));
    if (formErrors.phoneNumber)
      setFormErrors((prev) => ({ ...prev, phoneNumber: "" }));
  };

  // ── Validation ──
  const validate = () => {
    const errors = {};

    if (!form.name.trim()) {
      errors.name = "Full name is required";
    } else if (form.name.trim().length < 2) {
      errors.name = "Name must be at least 2 characters";
    }

    if (!form.email.trim()) {
      errors.email = "Email address is required";
    } else if (!isValidEmail(form.email)) {
      errors.email = "Please enter a valid email address";
    }

    if (!form.phoneNumber || form.phoneNumber.replace(/\D/g, "") === dialCode) {
      errors.phoneNumber = "Phone number is required";
    } else if (!isValidPhone(form.phoneNumber)) {
      errors.phoneNumber = "Please enter a valid phone number (7–15 digits)";
    }

    if (!form.comment.trim()) {
      errors.comment = "Please describe what you need a quote for";
    } else if (form.comment.trim().length < 10) {
      errors.comment = "Comment must be at least 10 characters";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // ── Submit ──
  const handleSubmit = async () => {
    if (!validate()) return;

    loader(true);
    const payload = {
      name: form.name.trim(),
      email: form.email.trim(),
      phoneNumber: form.phoneNumber,
      comment: form.comment.trim(),
    };

    await ApiClient.post("quote-request/add", payload)
      .then((res) => {
        if (res?.success) {
          swal({
            title: "Quote Request Submitted",
            text: "Your quote request has been sent successfully. You can track its status and supplier responses anytime from My Documents → Request to Quote.",
            icon: "success",
            className: "smallModal",
            buttons: {
              cancel: {
                text: "Continue Shopping",
                visible: true,
                className: "swal-btn-secondary",
              },
              confirm: {
                text: "View Details",
                className: "swal-btn-primary",
              },
            },
          }).then((viewDetails) => {
            if (viewDetails) {
              navigate("/orders");
            }
          });

          closeModal();
          if (typeof onSuccess === "function") onSuccess();
        }
      })
      .catch(console.error);

    loader(false);
  };

  return (
    <Transition appear show={open} as={Fragment}>
      <Dialog as="div" className="relative z-[999]" onClose={closeModal}>
        {/* Backdrop */}
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/30 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto scrollbar">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-2xl bg-white rounded-2xl shadow-xl flex flex-col max-h-[90vh]">
                {/* ── Modal Header ── */}
                <div className="flex items-center justify-between border-b border-gray-100 shrink-0 px-5 py-4">
                  <div className="flex items-center gap-3">
                    <Dialog.Title className="text-base font-semibold text-gray-900">
                      Request a Quote
                    </Dialog.Title>
                  </div>
                  <button
                    onClick={closeModal}
                    className="size-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-500 hover:text-gray-600 transition-colors"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M18 6 6 18M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {/* ── Modal Body ── */}
                <div className="overflow-y-auto scrollbar flex-1 space-y-5 px-5 py-5 ">
                  {/* Section label */}
                  <div className="space-y-1">
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest">
                      Your Information
                    </p>
                    <div className="pt-2 space-y-4">
                      {/* Full Name */}
                      <FormInput
                        label="Full Name"
                        name="name"
                        required
                        value={form.name}
                        onChange={handleChange}
                        error={formErrors.name}
                        placeholder="e.g. John Doe"
                      />

                      {/* Email */}
                      <FormInput
                        label="Email Address"
                        name="email"
                        type="email"
                        required
                        value={form.email}
                        onChange={handleChange}
                        error={formErrors.email}
                        placeholder="e.g. john@example.com"
                      />

                      {/* Phone Number — react-phone-input-2 */}
                      <div>
                        <label className="block text-sm text-gray-700 mb-1">
                          Phone Number <span className="text-red-400">*</span>
                        </label>
                        <PhoneInput
                          country="in"
                          value={form.phoneNumber}
                          onChange={handlePhoneChange}
                          enableSearch
                          searchPlaceholder="Search country..."
                          inputStyle={{
                            width: "100%",
                            height: "44px",
                            fontSize: "14px",
                            color: "#1f2937",
                            borderRadius: "12px",
                            border: formErrors.phoneNumber
                              ? "1px solid #f87171"
                              : "1px solid #e5e7eb",
                            paddingLeft: "48px",
                            outline: "none",
                            transition: "border-color 0.15s, box-shadow 0.15s",
                          }}
                          buttonStyle={{
                            borderRadius: "12px 0 0 12px",
                            border: formErrors.phoneNumber
                              ? "1px solid #f87171"
                              : "1px solid #e5e7eb",
                            borderRight: "none",
                            background: "#f9fafb",
                          }}
                          dropdownStyle={{
                            borderRadius: "12px",
                            boxShadow: "0 4px 24px rgba(0,0,0,0.10)",
                            fontSize: "13px",
                          }}
                          containerStyle={{ width: "100%" }}
                          inputProps={{
                            name: "phoneNumber",
                            required: true,
                          }}
                        />
                        {formErrors.phoneNumber && (
                          <span role="alert" className="text-xs text-[#c53030] mt-1 block">
                            {formErrors.phoneNumber}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Comment */}
                  <div className="space-y-1">
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest">
                      Your Request
                    </p>
                    <div className="pt-2">
                      <FormInput
                        label="Comment / Message"
                        name="comment"
                        textarea
                        required
                        value={form.comment}
                        onChange={handleChange}
                        error={formErrors.comment}
                        placeholder="e.g. I need a quote for PQR product, quantity 10 units..."
                      />
                    </div>
                  </div>

                  <div className="h-1" />
                </div>

                {/* ── Modal Footer ── */}
                <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-gray-100 shrink-0">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="flex gap-2 items-center bg-white text-gray-600 border rounded-full text-sm hover:scale-[1.04] duration-300 transition px-5 py-2.5"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleSubmit}
                    className="flex gap-2 items-center justify-center bg-gradient-to-br from-[#115B9D] to-[#116DAE] text-white rounded-full text-sm hover:scale-[1.04] duration-300 transition px-5 py-2.5"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="15"
                      height="15"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M22 2 11 13" />
                      <path d="M22 2 15 22 11 13 2 9l20-7z" />
                    </svg>
                    Submit Request
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default QuoteRequestModal;
